// Adapted from https://github.com/terra-project/terra-js/blob/master/src/utils/keyUtils.ts

import * as bip32 from 'bip32';
import * as bip39 from 'bip39';
import SHA256 from 'crypto-js/sha256';
import * as secp256k1 from 'secp256k1';
import { Key } from './Key';

export const LUNA_COIN_TYPE = 330;

interface MnemonicKeyOptions {
  mnemonic?: string;
  account?: number;
  index?: number;
  coinType?: number;
}

const DEFAULT_OPTIONS = {
  account: 0,
  index: 0,
  coinType: LUNA_COIN_TYPE,
};

export class MnemonicKey extends Key {
  public privateKey: Buffer;
  public mnemonic: string;

  constructor(options: MnemonicKeyOptions = {}) {
    const { account, index, coinType } = {
      ...DEFAULT_OPTIONS,
      ...options,
    };
    let { mnemonic } = options;
    if (mnemonic === undefined) {
      mnemonic = bip39.generateMnemonic(256);
    }
    const seed: Buffer = bip39.mnemonicToSeedSync(mnemonic);
    const masterKey = bip32.fromSeed(seed);
    const hdPathLuna = `m/44'/${coinType}'/${account}'/0/${index}`;
    const terraHD = masterKey.derivePath(hdPathLuna);
    const privateKey = terraHD.privateKey;

    if (!privateKey) {
      throw new Error('Failed to derive key pair');
    }

    const publicKey = secp256k1.publicKeyCreate(
      new Uint8Array(privateKey),
      true
    );

    super(Buffer.from(publicKey));
    this.privateKey = Buffer.from(privateKey);
    this.mnemonic = mnemonic;
  }

  public sign(payload: Buffer): Buffer {
    const hash = Buffer.from(SHA256(payload.toString()).toString(), 'hex');
    const { signature } = secp256k1.ecdsaSign(
      Uint8Array.from(hash),
      Uint8Array.from(this.privateKey)
    );
    return Buffer.from(signature);
  }
}
